import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AskNaviMessageComponent } from '../ask-navi-message/ask-navi-message.component';
import { Button } from 'primeng/button';
import { Splitter } from 'primeng/splitter';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { AskNaviChatStateService } from '../../services/ask-navi-chat-state.service';
import { AskNaviWelcomeComponent } from '../ask-navi-welcome/ask-navi-welcome.component';
import { Message } from '../../models/ask-navi-chat.models';

/**
 * Main Ask Navi chat panel: welcome screen, message thread, and composer.
 */
@Component({
  selector: 'an-ask-navi-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AskNaviMessageComponent,
    Button,
    Splitter,
    ScrollRegionComponent,
    AskNaviWelcomeComponent,
  ],
  templateUrl: './ask-navi-chat.component.html',
  styleUrl: './ask-navi-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AskNaviChatComponent implements AfterViewChecked {
  protected readonly state = inject(AskNaviChatStateService);

  private readonly _messagesEnd = viewChild<ElementRef<HTMLDivElement>>('messagesEnd');
  private _shouldScroll = false;

  ngAfterViewChecked(): void {
    if (this._shouldScroll) {
      this._messagesEnd()?.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this._shouldScroll = false;
    }
  }

  onPromptSelect(message: string): void {
    void this.state.sendMessage(message);
    this._shouldScroll = true;
  }

  onSend(): void {
    const text = this.state.draftInput();
    if (!text.trim()) return;
    void this.state.sendMessage(text);
    this._shouldScroll = true;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  onDraftChange(value: string): void {
    this.state.draftInput.set(value);
  }

  /** Hides the empty in-progress assistant row while the standalone thinking bubble is shown. */
  isHiddenStreamingPlaceholder(message: Message): boolean {
    return (
      this.state.showThinking() &&
      message.id === this.state.streamingMessageId() &&
      !message.content.trim()
    );
  }
}
